import { useEffect, useState, type CSSProperties, type RefObject } from 'react';

/** Clockwise display rotation (degrees) the backend resolves for a recording. */
export type VideoRotation = 0 | 90 | 180 | 270;

/** Anything outside the four quarter turns plays as recorded. */
export const toVideoRotation = (value: unknown): VideoRotation =>
  value === 90 || value === 180 || value === 270 ? value : 0;

// Until the stream reports its frame size, assume the phones' usual landscape 16:9.
const DEFAULT_ASPECT = 16 / 9;

/**
 * Scale that fits a quarter-turned picture back inside its own element box. The element keeps its
 * layout size and `object-contain` letterboxes the frame in it; the turn swaps the frame's sides, so
 * it shrinks in a landscape box and may grow in a portrait one.
 */
export const quarterTurnScale = (
  boxWidth: number,
  boxHeight: number,
  videoWidth: number,
  videoHeight: number,
): number => {
  if (boxWidth <= 0 || boxHeight <= 0) return 1;
  const aspect = videoWidth > 0 && videoHeight > 0 ? videoWidth / videoHeight : DEFAULT_ASPECT;
  const shownWidth = Math.min(boxWidth, boxHeight * aspect);
  const shownHeight = shownWidth / aspect;
  return Math.min(boxWidth / shownHeight, boxHeight / shownWidth);
};

/** The `<video>` style that turns the recording upright; undefined when there is nothing to turn. */
export const videoRotationStyle = (rotation: VideoRotation, scale: number): CSSProperties | undefined => {
  if (rotation === 0) return undefined;
  if (rotation === 180) return { transform: 'rotate(180deg)' };
  return { transform: `rotate(${rotation}deg) scale(${scale})` };
};

/**
 * [videoRotationStyle] for a mounted `<video>`, re-measured when its box or its stream's frame size
 * changes. Only the picture turns: the layout, and everything drawn over the video, stays as it is.
 */
export const useVideoRotationStyle = (
  videoRef: RefObject<HTMLVideoElement | null>,
  rotation: VideoRotation,
): CSSProperties | undefined => {
  const [scale, setScale] = useState(1);
  const quarterTurn = rotation === 90 || rotation === 270;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !quarterTurn) return;
    const measure = () =>
      setScale(quarterTurnScale(video.clientWidth, video.clientHeight, video.videoWidth, video.videoHeight));
    measure();
    // `resize` is the media event for a new frame size, e.g. a resolution switch.
    video.addEventListener('loadedmetadata', measure);
    video.addEventListener('resize', measure);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(video);
    return () => {
      video.removeEventListener('loadedmetadata', measure);
      video.removeEventListener('resize', measure);
      observer?.disconnect();
    };
  }, [videoRef, quarterTurn]);

  return videoRotationStyle(rotation, scale);
};

/** The device card's choices: Auto (stored as null, follow the phone) or a fixed angle. */
export type DeviceRotationChoice = 'auto' | '0' | '90' | '180' | '270';

export const deviceRotationChoice = (stored: number | null | undefined): DeviceRotationChoice =>
  stored === 0 || stored === 90 || stored === 180 || stored === 270
    ? (String(stored) as DeviceRotationChoice)
    : 'auto';

export const deviceRotationValue = (choice: DeviceRotationChoice): VideoRotation | null =>
  choice === 'auto' ? null : toVideoRotation(Number(choice));
